import ctl from '@netlify/classnames-template-literals'
import { usePrevious } from '@uidotdev/usehooks'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TimeAgo, Tooltip } from '../../rapid-ui'
import { editorRefreshCallbackAtom } from '../hooks/use-editor-refresh'
import {
	campaignDetailsSavingAtom,
	campaignReviewSavingAtom,
	feedAdSavingAtom,
	formSavingAtom,
	landingPageSavingAtom,
	storyAdSavingAtom,
} from '../state'

export function SaveStatusButton() {
	const { t } = useTranslation('common')
	const feedAdSaving = useAtomValue(feedAdSavingAtom)
	const storyAdSaving = useAtomValue(storyAdSavingAtom)
	const landingPageSaving = useAtomValue(landingPageSavingAtom)
	const formSaving = useAtomValue(formSavingAtom)

	const campaignSaving = useAtomValue(campaignDetailsSavingAtom)
	const campaignReviewSaving = useAtomValue(campaignReviewSavingAtom)

	const allStates = [
		feedAdSaving,
		storyAdSaving,
		landingPageSaving,
		formSaving,
		campaignSaving,
		campaignReviewSaving,
	]

	const hasError = allStates.some((state) => state === 'error')
	const isSaving = allStates.some(
		(state) => state === 'debouncing' || state === 'saving',
	)
	const previousIsSaving = usePrevious(isSaving)

	const [lastSaved, setLastSaved] = useState<Date | null>(null)

	useEffect(() => {
		if (previousIsSaving && !isSaving && !hasError) {
			setLastSaved(new Date())
		}
	}, [isSaving, hasError])

	const onRefresh = useAtomValue(editorRefreshCallbackAtom)
	const [isRefreshing, setIsRefreshing] = useState(false)

	const handleRefresh = useCallback(async () => {
		if (!onRefresh || isSaving) { return }
		setIsRefreshing(true)
		try {
			await onRefresh()
		} finally {
			setIsRefreshing(false)
		}
	}, [onRefresh, isSaving])

	return (
		<div className="flex items-center gap-3">
			<div>
				{hasError
					? (
						<div className="text-sm text-red-500 tracking-normal">
							{t('editor.save.failed')}
						</div>
					)
					: isSaving
					? (
						<div className="text-sm text-grey-400 tracking-normal animate-pulse">
							{t('editor.save.autosaving')}
						</div>
					)
					: (
						<div className="text-sm text-grey-400 tracking-normal">
							{t('editor.save.saved')} {lastSaved
								? (
									<>
										<TimeAgo date={lastSaved} short ago />
									</>
								)
								: ''}
						</div>
					)}
			</div>
			<Tooltip
				content={
					<div className="text-xs">
						{t('editor.save.refreshTooltip')}
					</div>
				}
			>
				<button
					onClick={handleRefresh}
					disabled={isSaving || isRefreshing || !onRefresh}
					type="button"
					className={ctl(`
						rounded p-1.5 transition-all duration-200
						${
						(!isSaving && !isRefreshing && onRefresh)
							? 'hover:bg-grey-100 cursor-pointer text-grey-400 hover:text-grey-800'
							: 'opacity-30 cursor-not-allowed text-grey-400'
					}
					`)}
				>
					<RefreshIcon isSpinning={isRefreshing} />
				</button>
			</Tooltip>
		</div>
	)
}

function RefreshIcon({ isSpinning }: { isSpinning: boolean }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="currentColor"
			className={ctl(`
				size-4
				${isSpinning ? 'animate-spin' : ''}
			`)}
		>
			<path
				fillRule="evenodd"
				d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z"
				clipRule="evenodd"
			/>
		</svg>
	)
}
